/**
 * Converts an SRT subtitle string to a WebVTT string.
 * If the input already contains a WEBVTT header, it is returned unchanged.
 *
 * @param {string} subtitleText
 * @returns {string}
 */
export function ConvertToVtt(subtitleText) {
  const normalizedText = subtitleText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  if (/^\uFEFF?\s*WEBVTT\b/i.test(normalizedText)) {
    return subtitleText;
  }

  const body = normalizedText
    .trim()
    // Replace comma in timestamps (e.g. 00:01:02,500 -> 00:01:02.500)
    .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');

  return `WEBVTT\n\n${body}`;
}

/**
 * Returns a human-readable label for an ISO 639-1 language code using the
 * browser's Intl API. Falls back to the code itself when the locale is unknown.
 *
 * @param {string} langCode
 * @returns {string}
 */
export function languageLabel(langCode) {
  try {
    const display = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = display.of(langCode);
    return name || langCode;
  } catch {
    return langCode;
  }
}

/**
 * Fetches a subtitle file and returns its text content.
 *
 * @param {string} url
 * @returns {Promise<string>}
 */
async function fetchSubtitleText(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch subtitle: ${response.status} ${url}`);
  }
  return response.text();
}

/**
 * Given a subtitle record from the backend ({ language, path }), resolves a
 * Video.js-compatible text track descriptor. When the file is `.srt`, it is
 * fetched, converted to VTT, and a temporary Blob URL is created.
 *
 * Returns an object with:
 *   - track  : Video.js RemoteTextTrack options object
 *   - blobUrl: the generated Blob URL (null when the original src is used)
 *
 * @param {{ language: string, path: string }} subtitle
 * @param {boolean} [isFirst=false]  Whether this is the first/default track
 * @returns {Promise<{ track: object, blobUrl: string|null }>}
 */
export async function resolveSubtitleTrack(subtitle, isFirst = false) {
  const { language, path } = subtitle;
  const label = languageLabel(language);

  let src = path;
  let blobUrl = null;

  const subtitleText = await fetchSubtitleText(path);
  const vttText = ConvertToVtt(subtitleText);
  blobUrl = URL.createObjectURL(new Blob([vttText], { type: 'text/vtt' }));
  src = blobUrl;
  
  const track = {
    kind: 'subtitles',
    src,
    srclang: language,
    label,
    default: isFirst,
  };

  return { track, blobUrl };
}

/**
 * Resolves all subtitle records for a video object, returning an array of
 * resolved track descriptors and their associated blob URLs.
 *
 * Skips entries with missing or unsupported file types (only `.srt` and `.vtt`
 * are accepted).
 *
 * @param {Array<{ language: string, path: string }>} subtitles
 * @returns {Promise<{ tracks: object[], blobUrls: string[] }>}
 */
export async function resolveSubtitleTracks(subtitles) {
  if (!Array.isArray(subtitles) || subtitles.length === 0) {
    return { tracks: [], blobUrls: [] };
  }

  const validEntries = subtitles.filter(
    (s) => s?.language && s?.path
  );

  const results = await Promise.all(
    validEntries.map((s, index) => resolveSubtitleTrack(s, index === 0))
  );

  const tracks = results.map((r) => r.track);
  const blobUrls = results.map((r) => r.blobUrl).filter(Boolean);

  return { tracks, blobUrls };
}

/**
 * Revokes all Blob URLs to free up memory.
 *
 * @param {string[]} blobUrls
 */
export function revokeSubtitleBlobUrls(blobUrls) {
  blobUrls.forEach((url) => URL.revokeObjectURL(url));
}
